// Pure pieces of the quantities matrix (stores/quantities.js), kept free of
// Pinia and of imports so tests/unit/quantity_counts.test.js can run them.
//
// A count row is { design, phase, batch?, count }: `design` and `batch` are
// uuids, and `phase` is the phase uuid (utils/phase_keys.js). Phase rows carry
// no batch; they are the target the batch rows of the same phase add up to.

// Normalised key for the count indexes. Phase counts carry no batch, and a count
// loaded from a saved project may have no `batch` key at all, so null/undefined
// must collapse to the same thing.
export function countKey(design, phase, batch) {
    return `${design}|${phase ?? ""}|${batch ?? ""}`
}

export function columnKey(phase, batch) {
    return `${phase ?? ""}|${batch ?? ""}`
}

export function buildCountIndex(counts) {
    const index = new Map()
    for (const c of counts) index.set(countKey(c.design, c.phase, c.batch), c)
    return index
}

// Rebuilds both count arrays by tallying the units that actually exist, under
// each unit's batch and that batch's phase.
export function countersFromActuals(actuals, batches_by_uuid) {
    const batch_index = new Map()
    const phase_index = new Map()

    for (const actual of actuals) {
        const batch = batches_by_uuid.get(actual.batch)
        const phase = batch?.phase_uuid || ""
        const design = actual.design

        const batch_key = countKey(design, phase, batch?.uuid)
        const batch_entry = batch_index.get(batch_key)
        if (batch_entry) batch_entry.count += 1
        else batch_index.set(batch_key, { phase, batch: batch?.uuid, design, count: 1 })

        const phase_key = countKey(design, phase, undefined)
        const phase_entry = phase_index.get(phase_key)
        if (phase_entry) phase_entry.count += 1
        else phase_index.set(phase_key, { phase, design, count: 1 })
    }

    return {
        batch_counts: [...batch_index.values()],
        phase_counts: [...phase_index.values()],
    }
}

// For every (design, phase) the batch quantities must add up to the phase
// quantity. Returns what does not, as { design, phase, in_phase, in_batches,
// diff }. `phases` are the project's phase uuids; rows `skip` returns true for
// (stale rows) are left out of both sums.
export function countMismatches(batch_rows, phase_rows, phases, skip = () => false) {
    const batch_sums = new Map()
    const phase_sums = new Map()
    const designs = new Set()
    for (const c of batch_rows) {
        if (skip(c)) continue
        designs.add(c.design)
        const key = `${c.design}|${c.phase ?? ""}`
        batch_sums.set(key, (batch_sums.get(key) || 0) + c.count)
    }
    for (const c of phase_rows) {
        if (skip(c)) continue
        designs.add(c.design)
        const key = `${c.design}|${c.phase ?? ""}`
        phase_sums.set(key, (phase_sums.get(key) || 0) + c.count)
    }
    const mismatches = []
    for (const design of designs) {
        for (const phase of phases) {
            const key = `${design}|${phase ?? ""}`
            const in_batches = batch_sums.get(key) || 0
            const in_phase = phase_sums.get(key) || 0
            if (in_batches !== in_phase) {
                mismatches.push({ design, phase, in_phase, in_batches, diff: in_batches - in_phase })
            }
        }
    }
    return { designs, mismatches }
}
